import React, { useMemo, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { ProgressBar } from 'primereact/progressbar';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Chart } from 'primereact/chart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faMap } from '@fortawesome/free-solid-svg-icons';

import { Chart as ChartJS } from 'chart.js';

ChartJS.register(ChartDataLabels);

// Lazy loading del componente MiniMap
const MiniMap = lazy(() => import('./MiniMap'));


export const FieldCard = ({ field_predicciones, field_date, crop_name, plot_name, estado_vegetativo_valor, estado_vegetativo_color, worker_name, onClick, coordenadas, cargando }) => {
    const navigate = useNavigate();

    // Mostrar NDVI y evapotranspiración solo entre 1 de junio y 15 de septiembre (año actual)
    const showSeasonInfo = useMemo(() => {
        const today = new Date();
        const year = today.getFullYear();
        const seasonStart = new Date(year, 5, 1, 0, 0, 0, 0);
        const seasonEnd = new Date(year, 8, 15, 23, 59, 59, 999);
        return today >= seasonStart && today <= seasonEnd;
    }, []);

    const datosClimaAscendenteTable = useMemo(() => {
        if (!field_predicciones?.length) return [];
        return [...field_predicciones]
            .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
            .slice(2);
    }, [field_predicciones]);

    // Cálculo total para el riego: suma de todas las ET0 menos la suma de la precipitación.
    // Si el resultado es negativo, se mostrará 0.
    const totalRiego = useMemo(() => {
        if (!datosClimaAscendenteTable.length) return 0;
        const totalEt0 = datosClimaAscendenteTable.reduce((acc, item) => acc + item.et0, 0);
        const totalPrecipitacion = datosClimaAscendenteTable.reduce((acc, item) => acc + item.precipitacion, 0);
        const diff = totalEt0 - totalPrecipitacion;
        return diff < 0 ? 0 : Math.round(diff * 100) / 100;
    }, [datosClimaAscendenteTable]);

    const totalEt0 = useMemo(() => {
        if (!datosClimaAscendenteTable.length) return 0;
        const totalEt0 = datosClimaAscendenteTable.reduce((acc, item) => acc + item.et0, 0);
        return Math.round(totalEt0 * 0.5 * 10) / 10;
    }, [datosClimaAscendenteTable]);

    const formatDate = (dateString) => {
        if (dateString == null) {
            return 'No hay IV';
        }
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = date.toLocaleString('default', { month: 'short' });
        return `${day}-${month}`;
    };
    
    return (
        <Card 
            className={`w-full mt-3`}
            style={{ 
                backgroundColor: 'white', 
                borderRadius: '10px',
                border: 'solid',
                borderWidth: '1px',
                borderColor: '#DEE2E6',
                boxShadow: '4px 4px 8px rgba(0, 0, 0, 0.3)',
                maxWidth: '450px'
            }}
        >
            <div className='p-3'>
                {cargando && (
                    <div className='flex align-items-center gap-2 mb-2 p-2' style={{ backgroundColor: '#fff8e1', borderRadius: '8px', border: '1px solid #ffe082' }}>
                        <i className='pi pi-spin pi-spinner' style={{ color: '#f59e0b', fontSize: '14px' }} />
                        <span style={{ fontSize: '12px', color: '#92400e' }}>Cargando datos del campo...</span>
                    </div>
                )}
                <div className='flex justify-content-between align-items-center mb-1'>
                    <span 
                        style={{ 
                            color: 'black', 
                            whiteSpace: 'nowrap', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            minWidth: 0,
                            flex: 1
                        }} 
                        className='font-bold text-xl'
                    >
                        {plot_name}
                    </span>
                    {/*<span className='flex align-items-center justify-content-end text-sm' style={{ color: 'black', minWidth: '70px', marginTop: '6px' }}>{formatDate(field_date)}</span>*/}
                    <span 
                        style={{ 
                            color: 'black', 
                            fontSize: '14px', 
                            textDecoration: 'underline', 
                            cursor: 'pointer',
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                            marginLeft: '8px'
                        }}
                        onClick={onClick}
                    >
                        <FontAwesomeIcon style={{ color: 'black', fontSize: '14px' }} icon={faMap} className='mr-2' />Ver ficha
                    </span>
                </div>
                <div className='flex justify-content-between align-items-center mb-2'>
                    <span style={{ color: 'black'}} className='text-sm'>
                        {crop_name}
                    </span>
                </div>

                {showSeasonInfo && (
                    <div className='flex flex-column'>
                        <span style={{ color: 'black', fontWeight: 600 }}>Estado vegetativo a {field_date ? new Date(field_date).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}</span>
                        <span style={{ color: 'black', fontWeight: 300, fontSize: '12px' }}>* Respecto al potencial máximo histórico</span>
                        <ProgressBar 
                            value={estado_vegetativo_valor === 0 ? 0 : (estado_vegetativo_valor < 10 ? 10 : estado_vegetativo_valor)} 
                            style={{ borderRadius: '15px', marginTop: '10px' }}
                            color={estado_vegetativo_color}
                            showValue={true}
                            displayValueTemplate={() => (
                                estado_vegetativo_valor === 0 ? null : (
                                    <span 
                                        style={{ 
                                            fontSize: '14px', 
                                            fontWeight: '500',
                                            color: 'black',
                                            textShadow: `
                                                -0.5px -0.5px 0 white, 
                                                0.5px -0.5px 0 white, 
                                                -0.5px 0.5px 0 white, 
                                                0.5px 0.5px 0 white`
                                        }}
                                    >
                                        {`${estado_vegetativo_valor}%`}
                                    </span>
                                )
                            )}
                        />
                        <span className='mt-2' style={{ color: 'black', fontWeight: '600' }}>Evapotranspiración</span>
                        <span style={{ color: 'black'}} className='text-sm mt-1'>La evapotranspiración de los proximos 7 días se estima en {totalEt0} mm.</span>
                    </div>   
                )}
                
                {/* Mini mapa de la parcela */}
                {coordenadas && (
                    <div style={{ 
                        width: '100%', 
                        height: '150px', 
                        borderRadius: '8px', 
                        overflow: 'hidden',
                        marginTop: '12px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                        <Suspense fallback={
                            <div style={{ 
                                width: '100%', 
                                height: '100%', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                background: '#f0f0f0'
                            }}>
                                <span style={{ color: '#666', fontSize: '12px' }}>Cargando mapa...</span>
                            </div>
                        }>
                            <MiniMap coordenadas={coordenadas} />
                        </Suspense>
                    </div>
                )}
            </div>
            <div style={{ backgroundColor: '#f0f0f0', borderRadius: '0px 0px 10px 10px' }} className='w-full flex align-items-center pl-3 pr-3 pt-2 pb-2'>
                <FontAwesomeIcon style={{ color: 'black', fontSize: '14px' }} icon={faUser} className='mr-2' />
                <span 
                    style={{ 
                        color: 'black', 
                        fontSize: '14px', 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis'
                    }}
                >
                    {worker_name}
                </span>
            </div>
        </Card>
    );
};